'use client'

import { askSage, canAskSage, onAskSageAvailability } from '@/lib/tutor/on-screen-material'
import { useState, useRef, useEffect, useSyncExternalStore } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { parseSageAction, type SageAction } from '@unlimitedtests/core'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { MessageCircle, X, Send, Loader2, Lock, ThumbsUp, ThumbsDown } from 'lucide-react'
import { AssistantMarkdown } from './tutor/TutorChatPanel'
import SageActionCard from './tutor/SageActionCard'
import { canAccessAITutor, type PlanKey } from '@/lib/billing/plan-config'
import { parseSageQuotaHeaders, sageNearLimitNotice } from '@/lib/tutor/sage-quota'
import { savedTranscript } from '@/lib/tutor/saved-transcript'
import { useSchoolAiBlocked } from '@/components/school/SchoolAiAccess'

type Message = {
  role: 'user' | 'assistant'
  content: string
  /** Priming message that triggers the opening explanation; sent to the API but not displayed. */
  hidden?: boolean
  /**
   * The Practice button the server built for this reply (the route's action
   * frame), already through parseSageAction. Display only — never sent back.
   */
  action?: SageAction | null
  /**
   * The route's `similar` frame: the student asked for another question and the
   * page can open a vetted one on the same skill in place. Display only.
   */
  similar?: { skill: string } | null
}

/**
 * Pages where this chat sits inside a session the student is still running.
 *
 * The Practice button opens a NEW practice session, which here would pull the
 * student out of the one they are in: the adaptive session lives only in page
 * state, and /practice/session keeps a single resume snapshot that the new
 * session would overwrite. And mid-session on a topic, "practise this topic"
 * is the thing they are already doing. Same rule as the phone, where Sage on
 * a running mission is an overlay and gets no button.
 */
const RUNNING_SESSION_PATHS = ['/practice/session', '/practice/adaptive']

/**
 * Whether to ask /api/tutor/chat for a Practice button on this page. Asking is
 * what lets the server tell Sage a button is there, so a page that wouldn't
 * show one must not ask.
 */
export function offersPracticeOn(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return !RUNNING_SESSION_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

type TutorChatProps = {
  questionId: string
  questionStem: string
  correctAnswer: string
  /** Whether the student answered correctly — drives the tutor's framing (right vs wrong). */
  isCorrect?: boolean
  /** The answer the student picked, so the tutor can address their specific choice. */
  userAnswer?: string
  /** User's subscription plan */
  plan: string
  /** If provided, show inline instead of as a button-triggered panel */
  inline?: boolean
  /**
   * Block opening the panel. Used when the surrounding surface is about to
   * replace this question (e.g. a "similar question" fetch is in flight): the
   * panel lives inside the answered-feedback block, so opening it there would
   * spend a metered Sage message on a conversation that's about to be unmounted
   * mid-stream.
   */
  disabled?: boolean
  /**
   * Opens a bank question on the same skill in place (practice's "Another
   * similar question"). When given, asking Sage for "another one" puts a
   * "Try a similar question" button under the reply instead of Sage writing a
   * question of its own — interactive, with feedback, and recorded.
   */
  onTrySimilar?: () => void
}

const MAX_MESSAGES = 10

export default function TutorChat(props: TutorChatProps) {
  // The school has turned the AI tutor off: no chat under the question.
  if (useSchoolAiBlocked()) return null
  return <TutorChatInner {...props} />
}

function TutorChatInner({
  questionId,
  questionStem,
  correctAnswer,
  isCorrect,
  userAnswer,
  plan,
  inline = false,
  disabled = false,
  onTrySimilar,
}: TutorChatProps) {
  const [open, setOpen] = useState(inline)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const router = useRouter()
  const offerPractice = offersPracticeOn(usePathname())
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const primedRef = useRef(false)
  // Client-owned id for this saved practice-tutor session (set on first persist).
  const sessionIdRef = useRef<string | null>(null)
  // Per-(visible)-message thumbs rating: 1 = up, -1 = down.
  const [feedback, setFeedback] = useState<Record<number, 1 | -1>>({})
  // Remaining daily Sage quota (read off the response headers). null = unknown
  // (e.g. ADMINs, who are uncapped) → no fair-use notice shown.
  const [sageRemaining, setSageRemaining] = useState<number | null>(null)
  const [sageCap, setSageCap] = useState<number | null>(null)

  // Stick to the bottom only when the user is already near it, scrolling the
  // message container itself (not the page) — no jumpy page-scroll per token.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    if (nearBottom) el.scrollTop = el.scrollHeight
  }, [messages])

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus()
    }
  }, [open])

  // Packaging v2: Sage is available to everyone, metered by tier (the server
  // enforces Free 3/day · Pro 50/day). Use the SAME gate as the API + the rest
  // of the app — the old hardcoded `=== 'PREMIUM'` locked out every user.
  const hasTutorAccess = canAccessAITutor(plan as PlanKey)
  // The hidden priming message doesn't count toward the student's message budget.
  const userMessageCount = messages.filter((m) => m.role === 'user' && !m.hidden).length
  const atLimit = userMessageCount >= MAX_MESSAGES
  // Hidden priming messages are sent to the API but never rendered.
  const visibleMessages = messages.filter((m) => !m.hidden)
  // The Practice button shows under the LATEST reply only. Every reply about
  // this question carries the same button, so repeating it up the transcript
  // would be noise.
  let lastAssistantIndex = -1
  visibleMessages.forEach((m, i) => {
    if (m.role === 'assistant') lastAssistantIndex = i
  })
  // Fair-use heads-up as the student nears their daily Sage cap (null = nothing
  // to say yet / uncapped admin).
  const sageNotice = sageNearLimitNotice(sageRemaining, sageCap)
  // "Continue in Sage": the main chat, anchored to this question, with this
  // conversation carried over (question-coaching.ts). Only where the floating
  // Sage can open, and once there's something to continue.
  const mainSageHere = useSyncExternalStore(onAskSageAvailability, canAskSage, () => false)
  const canContinue =
    mainSageHere && !streaming && visibleMessages.some((m) => m.role === 'assistant' && m.content)
  async function continueInSage() {
    // Saved first, so the main chat can read it.
    await persistSession(messages)
    askSage({ material: {}, draft: '', questionId })
  }

  // Explain-first: once the panel is open (inline for a wrong answer, or on
  // click for a correct one), proactively lead with an explanation instead of
  // waiting for the student to type. The hidden priming message triggers the
  // model; the system prompt frames it by correctness (right vs wrong).
  useEffect(() => {
    if (!open || !hasTutorAccess || primedRef.current) return
    if (messages.length > 0 || streaming) return
    primedRef.current = true
    // sendMessage is stable (hoisted); the primedRef + messages guards above
    // make re-runs on other deps a no-op, so open/hasTutorAccess suffice.
    void sendMessage('Please explain this question.', { hidden: true })
  }, [open, hasTutorAccess])

  async function sendMessage(textArg?: string, opts?: { hidden?: boolean }) {
    const text = (textArg ?? input).trim()
    if (!text || streaming || atLimit) return

    setError(null)
    const newMessages: Message[] = [
      ...messages,
      { role: 'user', content: text, hidden: opts?.hidden },
    ]
    setMessages(newMessages)
    // Only clear the input box for messages the student actually typed.
    if (textArg === undefined) setInput('')
    setStreaming(true)

    // A question that got NO answer comes back out of the thread and into the
    // box, to send again. Left in, the student saw it unanswered with an empty
    // box, retyped it, and the thread then carried it twice — the app already
    // did this; the website didn't (build-7 retest, F7). A partial answer is
    // kept: half a reply beats none.
    let answered = false
    const takeBack = () => {
      if (answered) return
      setMessages(messages)
      if (textArg === undefined) setInput((current) => (current.trim() ? current : text))
    }

    try {
      const res = await fetch('/api/tutor/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({
          questionId,
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
          isCorrect,
          userAnswer,
          offerPractice,
          similarInPlace: onTrySimilar !== undefined,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        if (data.error === 'rate_limited') {
          setError(data.message ?? 'Rate limit exceeded. Try again later.')
          // A daily-cap hit means 0 left today — reflect it in the notice.
          if (data.blockedBy === 'day') setSageRemaining(0)
        } else if (data.error === 'monthly_ai_limit') {
          setError(data.message ?? "You've reached your AI usage limit for this month.")
        } else {
          setError(data.message ?? data.error ?? `Error (${res.status})`)
        }
        takeBack()
        setStreaming(false)
        return
      }

      // Surface the remaining daily Sage quota (headers present on success).
      const quota = parseSageQuotaHeaders(res.headers)
      if (quota.remaining !== null) setSageRemaining(quota.remaining)
      if (quota.cap !== null) setSageCap(quota.cap)

      // Check if it's a fallback (non-streaming JSON response)
      const contentType = res.headers.get('content-type') ?? ''
      if (contentType.includes('application/json')) {
        const data = await res.json()
        answered = true
        const finalMessages: Message[] = [
          ...newMessages,
          { role: 'assistant', content: data.content },
        ]
        setMessages(finalMessages)
        setStreaming(false)
        void persistSession(finalMessages)
        return
      }

      // Streaming SSE response
      const reader = res.body?.getReader()
      if (!reader) {
        setError('Failed to read response stream.')
        takeBack()
        setStreaming(false)
        return
      }

      const decoder = new TextDecoder()
      let assistantContent = ''
      let assistantAction: SageAction | null = null
      let assistantSimilar: { skill: string } | null = null

      // Add empty assistant message that we'll update
      setMessages([...newMessages, { role: 'assistant', content: '' }])

      // Coalesce streamed tokens into ~20fps renders so the markdown re-parse +
      // auto-scroll don't fire on every token (the source of the jumpiness).
      let lastFlush = 0
      const FLUSH_MS = 50
      const flushAssistant = () => {
        setMessages((prev) => {
          const updated = [...prev]
          updated[updated.length - 1] = {
            role: 'assistant',
            content: assistantContent,
            action: assistantAction,
            similar: assistantSimilar,
          }
          return updated
        })
      }

      let buffer = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6).trim()
          if (payload === '[DONE]') break
          try {
            const parsed = JSON.parse(payload)
            if (parsed.text) {
              answered = true
              assistantContent += parsed.text
              const now = Date.now()
              if (now - lastFlush >= FLUSH_MS) {
                lastFlush = now
                flushAssistant()
              }
            }
            if (parsed.error) {
              setError(parsed.error)
            }
            // The server's Practice button, re-checked here as every surface
            // does before drawing one: a frame that fails the allow-list
            // renders nothing rather than a wrong link.
            if (parsed.action !== undefined) {
              assistantAction = parseSageAction(parsed.action) ?? assistantAction
            }
            if (parsed.similar && typeof parsed.similar.skill === 'string') {
              assistantSimilar = { skill: parsed.similar.skill.slice(0, 80) }
            }
          } catch {
            // skip malformed chunks
          }
        }
      }
      // The stream ended without a word of reply — the server sent only an
      // error frame. Same as a failed send: the question comes back out of the
      // thread (with the empty reply bubble) and into the box. It used to stay,
      // with a blank reply under it, and retyping it duplicated the question
      // (build-7 retest of OTA ec269a59).
      if (!answered) {
        takeBack()
        setError((current) => current ?? 'Sage could not answer that. Please try again.')
        return
      }
      // Final flush — the throttle may have skipped the last tokens.
      flushAssistant()
      void persistSession([...newMessages, { role: 'assistant', content: assistantContent }])
    } catch (err) {
      setError('Network error. Please try again.')
      takeBack()
    } finally {
      setStreaming(false)
    }
  }

  // Persist the visible transcript so practice-tutor sessions are saved for
  // analysis. Best-effort — a failed save never blocks the chat.
  function persistSession(allMessages: Message[]) {
    if (!sessionIdRef.current) {
      sessionIdRef.current =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`
    }
    const transcript = allMessages
      .filter((m) => !m.hidden)
      .map((m) => ({ role: m.role, content: m.content }))
    if (transcript.length === 0) return Promise.resolve()
    // The newest 40 and the whole count: sending the full thread was a 400
    // from the 21st exchange, so a long chat silently stopped being saved.
    const saved = savedTranscript(transcript)
    return fetch('/api/tutor/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify({
        id: sessionIdRef.current,
        questionId,
        isCorrect,
        userAnswer,
        messages: saved.messages,
        revision: saved.revision,
      }),
    }).catch(() => {})
  }

  // Thumbs up/down on a specific assistant reply. Clicking the active rating
  // again clears it. Records to the saved session for quality analysis.
  async function rateMessage(index: number, value: 1 | -1) {
    if (!hasTutorAccess) return
    const next = feedback[index] === value ? 0 : value
    setFeedback((prev) => {
      const updated = { ...prev }
      if (next === 0) delete updated[index]
      else updated[index] = next
      return updated
    })
    // Make sure the session row exists (idempotent) before attaching feedback.
    await persistSession(messages)
    await fetch('/api/tutor/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify({
        sessionId: sessionIdRef.current,
        messageIndex: index,
        rating: next,
        messageContent: visibleMessages[index]?.content ?? '',
      }),
    }).catch(() => {})
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // PREMIUM gate — upgrade CTA for non-Premium users. This was a disabled
  // button (a dead end mid-practice); it now routes to pricing carrying the
  // current URL as returnTo, so after the trial checkout the student lands
  // back HERE — and the practice page restores their in-flight session from
  // its snapshot (see src/lib/practice-session-store.ts).
  if (!hasTutorAccess) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 border-amber-700/50 text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30"
        onClick={() => {
          const here = window.location.pathname + window.location.search
          router.push(`/pricing?returnTo=${encodeURIComponent(here)}`)
        }}
      >
        <Lock className="h-3.5 w-3.5" />
        Upgrade to Pro for more Sage messages
      </Button>
    )
  }

  // Closed state — just show trigger button
  if (!open) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 border-brand/50 text-brand-ink hover:bg-brand/10 dark:text-brand-ink dark:hover:bg-brand/40"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={disabled ? 'Loading the next question…' : undefined}
      >
        <MessageCircle className="h-3.5 w-3.5" />
        Ask Sage
      </Button>
    )
  }

  return (
    <Card className="mt-3 border-brand/50 bg-card/80">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-brand-ink" aria-hidden="true" />
          <span className="text-sm font-medium text-brand-ink">Sage</span>
        </div>
        {!inline && (
          <button
            onClick={() => setOpen(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close chat"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Question context */}
      <div className="border-b border-border bg-background/40 px-4 py-2">
        <p className="text-xs text-muted-foreground">Question context:</p>
        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{questionStem}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Correct answer:{' '}
          <span className="text-green-600 dark:text-green-400">{correctAnswer}</span>
        </p>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="h-80 overflow-y-auto px-4 py-3 space-y-3">
        {visibleMessages.length === 0 && !streaming && (
          <p className="text-center text-xs text-muted-foreground">
            Ask anything about this question.
          </p>
        )}

        {visibleMessages.map((msg, i) => {
          if (msg.role === 'user') {
            return (
              <div key={i} className="flex justify-end">
                <div className="max-w-[85%] rounded-lg bg-slate-200 px-3 py-2 text-sm text-slate-900 dark:bg-slate-700 dark:text-slate-100">
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                </div>
              </div>
            )
          }
          const isStreamingLast = streaming && i === visibleMessages.length - 1
          return (
            <div key={i} className="flex justify-start">
              <div className="flex max-w-[85%] flex-col items-start gap-1">
                <div className="rounded-lg border border-brand/40 bg-brand/10 px-3 py-2 text-sm text-brand-ink dark:bg-brand/25 dark:text-brand-ink">
                  <AssistantMarkdown content={msg.content} />
                  {isStreamingLast && (
                    <span className="ml-0.5 inline-block h-4 w-1 animate-pulse bg-brand" />
                  )}
                </div>
                {/* Per-response thumbs up/down — saved for tutor-quality analysis. */}
                {!isStreamingLast && msg.content.trim().length > 0 && (
                  <div className="flex items-center gap-0.5 pl-1">
                    <button
                      type="button"
                      onClick={() => rateMessage(i, 1)}
                      aria-label="Helpful"
                      aria-pressed={feedback[i] === 1}
                      className={`rounded p-1 transition-colors ${
                        feedback[i] === 1
                          ? 'bg-brand/15 text-brand-ink ring-1 ring-brand/50 dark:bg-brand/25 dark:text-brand-ink dark:ring-brand/50'
                          : 'text-muted-foreground hover:text-brand-ink dark:hover:text-brand-ink'
                      }`}
                    >
                      <ThumbsUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => rateMessage(i, -1)}
                      aria-label="Not helpful"
                      aria-pressed={feedback[i] === -1}
                      className={`rounded p-1 transition-colors ${
                        feedback[i] === -1
                          ? 'bg-red-100 text-red-700 ring-1 ring-red-400 dark:bg-red-900/60 dark:text-red-300 dark:ring-red-500/60'
                          : 'text-muted-foreground hover:text-red-600 dark:hover:text-red-400'
                      }`}
                    >
                      <ThumbsDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                {offerPractice && i === lastAssistantIndex && !isStreamingLast && msg.action && (
                  <SageActionCard action={msg.action} className="mt-1 w-full" />
                )}
                {onTrySimilar && i === lastAssistantIndex && !isStreamingLast && msg.similar && (
                  <button
                    type="button"
                    onClick={onTrySimilar}
                    data-testid="sage-try-similar"
                    className="mt-1 w-full rounded-lg border border-brand/40 bg-brand/10 px-3 py-2 text-left text-sm font-medium text-brand-ink transition-colors hover:bg-brand/20"
                  >
                    Try a similar question
                    <span className="block text-xs font-normal text-muted-foreground">
                      {msg.similar.skill} · from the question bank · counts toward your progress
                    </span>
                  </button>
                )}
              </div>
            </div>
          )
        })}

        {streaming && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
          <div className="flex justify-start">
            <div className="rounded-lg bg-brand/10 dark:bg-brand/25 border border-brand/40 px-3 py-2 text-sm text-brand-ink">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="mx-4 mb-2 rounded border border-red-800/50 bg-red-50 dark:bg-red-950/30 px-3 py-1.5 text-xs text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Fair-use daily-quota heads-up (suppressed while an error is showing to
          avoid duplicate messaging). */}
      {!error && sageNotice && (
        <div className="mx-4 mb-2 rounded border border-amber-300/60 bg-amber-50 px-3 py-1.5 text-xs text-amber-700 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300">
          {sageNotice}
        </div>
      )}

      {canContinue && (
        <div className="mx-4 mb-2 text-right">
          <button
            type="button"
            onClick={() => void continueInSage()}
            data-testid="sage-continue-main"
            className="text-xs font-medium text-brand-ink underline-offset-2 hover:underline"
          >
            Continue in Sage →
          </button>
          <span className="sr-only"> (opens your main Sage chat with this conversation)</span>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border px-3 py-2">
        {atLimit ? (
          <p className="text-center text-xs text-muted-foreground">
            Conversation limit reached ({MAX_MESSAGES} messages). Start a new chat to continue.
          </p>
        ) : (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this question..."
              disabled={streaming}
              className="flex-1 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/50 disabled:opacity-50"
            />
            <Button
              size="sm"
              onClick={() => sendMessage()}
              disabled={!input.trim() || streaming}
              className="h-9 w-9 shrink-0 bg-brand p-0 hover:bg-brand-hover"
              aria-label="Send message"
            >
              {streaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
